import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { LinkshimModule } from '@freelancer/components/linkshim';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { LinkUnderline, LinkWeight } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import { isEqual } from '@freelancer/utils';
import type { Match } from 'autolinker';
import Autolinker from 'autolinker';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import type { InteractiveNode, TextEntity } from './dynamic-text.types';

type LinkMatch = Match & { getUrl(): string; protocolRelativeMatch: boolean };

@Component({
  selector: 'fl-dynamic-text',
  standalone: true,
  imports: [CommonModule, UiModule, TrackingModule, LinkshimModule],
  template: `
    <ng-container
      *ngFor="let node of nodes$ | flAsync; trackBy: trackByContent"
    >
      <ng-container [ngSwitch]="node.type">
        <ng-container *ngSwitchCase="'text'">
          {{ node.text }}
        </ng-container>

        <ng-container *ngSwitchCase="'link'">
          <fl-linkshim
            *ngIf="useLinkShim && node.url"
            flTrackingLabel="linkifiedLinkShim"
            [link]="node.url"
          >
            {{ node.text }}
          </fl-linkshim>

          <fl-link
            *ngIf="!useLinkShim && node.url"
            flTrackingLabel="linkifiedLink"
            [link]="node.url"
          >
            {{ node.text }}
          </fl-link>
        </ng-container>

        <ng-container *ngSwitchCase="'entity'">
          <fl-link
            *ngIf="node.url"
            flTrackingLabel="TextEntityLink"
            [link]="node.url"
            [size]="TextSize.INHERIT"
            [weight]="LinkWeight.BOLD"
            [underline]="LinkUnderline.NEVER"
          >
            {{ node.text }}
          </fl-link>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./dynamic-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicTextComponent implements OnInit {
  TextSize = TextSize;
  LinkWeight = LinkWeight;
  LinkUnderline = LinkUnderline;

  /**
   * The text to be made interactive.
   */
  @Input() text$: Observable<string | undefined>;

  /**
   * An array of text entities to be used for mention links.
   */
  @Input() textEntities$: Observable<readonly TextEntity[]> = of([]);

  /**
   * If set to true, we will use fl-linkshim instead of fl-link.
   */
  @Input() useLinkShim = true;

  /**
   * The content of the text to be rendered on the screen.
   */
  nodes$: Observable<readonly InteractiveNode[]>;

  private autoLinker = new Autolinker({
    urls: true,
    email: false,
    phone: false,
  });

  ngOnInit(): void {
    this.nodes$ = combineLatest([
      this.text$.pipe(distinctUntilChanged(isEqual)),
      this.textEntities$.pipe(distinctUntilChanged(isEqual), startWith([])),
    ]).pipe(
      map(([text, entities]) => {
        if (!text) {
          return [];
        }

        return this.processText(text, entities);
      }),
    );
  }

  private processText(
    text: string,
    entities: readonly TextEntity[],
  ): readonly InteractiveNode[] {
    const entityNodes = this.getEntityNodes(text, entities);
    const nodes: InteractiveNode[] = [];

    entityNodes.forEach(node => {
      // If the node is an entity, we don't need to do anything with it.
      if (node.type === 'entity') {
        nodes.push(node);
        return;
      }

      // If the node is text, we need to check if it has any links in it.
      if (node.type === 'text') {
        const links = this.autoLinker.parse(node.text).filter(this.checkUrl);
        let cursor = 0;

        links.forEach(match => {
          const txt = node.text.substring(cursor, match.getOffset());
          if (txt) {
            nodes.push({
              type: 'text',
              text: txt,
            });
          }

          nodes.push({
            type: 'link',
            text: match.getMatchedText(),
            url: `${
              match.protocolRelativeMatch ? 'https:' : ''
            }${match.getUrl()}`,
          });

          cursor = match.getOffset() + match.getMatchedText().length;
        });

        const remainingText = node.text.substring(cursor);
        if (remainingText) {
          nodes.push({
            type: 'text',
            text: remainingText,
          });
        }
      }
    });

    return nodes;
  }

  private getEntityNodes(
    content: string,
    textEntities: readonly TextEntity[],
  ): readonly InteractiveNode[] {
    let lastPosition = 0;
    const nodes: InteractiveNode[] = [];

    textEntities.forEach(textEntity => {
      if (lastPosition < textEntity.offset) {
        const text = content.substring(lastPosition, textEntity.offset);
        if (text.length > 0) {
          nodes.push({
            type: 'text',
            text,
          });
        }
      }

      nodes.push({
        type: 'entity',
        text: content.substring(
          textEntity.offset,
          textEntity.offset + textEntity.length,
        ),
        url: textEntity.url,
      });

      lastPosition = textEntity.offset + textEntity.length;
    });

    if (lastPosition < content.length) {
      nodes.push({
        type: 'text',
        text: content.substring(lastPosition, content.length),
      });
    }

    return nodes;
  }

  private checkUrl(match: Match): match is LinkMatch {
    return match.getType() === 'url';
  }

  trackByContent(_: number, item: InteractiveNode): string {
    return `${item.type}_${item.text}`;
  }
}
